import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import type { ContentProfile } from '@content-planner/shared';
import { ContentProfile as ContentProfileSchema } from '@content-planner/shared';
import { ProgressBar } from './ProgressBar.js';
import { StepRound1 } from './StepRound1.js';
import { StepRound2 } from './StepRound2.js';
import { StepGreat8 } from './StepGreat8.js';
import { StepRound3 } from './StepRound3.js';
import { draftStorage } from '../../lib/storage.js';

type FormShape = ContentProfile & { month?: string };

const TOTAL_STEPS = 4;
const GREAT8_STEP = 3;

const STEP_FIELDS: Record<number, Array<keyof FormShape>> = {
  1: ['businessType', 'businessTypeOther', 'audience', 'audienceOther', 'goal'],
  2: ['platforms', 'tone', 'avoid'],
  3: ['great8', 'businessDescription'],
  4: ['month'],
};

export function WizardShell() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormShape>({
    mode: 'onTouched',
    defaultValues: {
      platforms: [],
      ...(draftStorage.load()?.profile ?? {}),
      month: draftStorage.load()?.month,
    },
  });

  const great8Empty = (form.watch('great8') ?? '').trim() === '';

  // Persist draft as the user types so a refresh doesn't wipe answers
  useEffect(() => {
    const sub = form.watch((values) => {
      draftStorage.save({
        profile: values as Partial<ContentProfile>,
        month: values.month,
      });
    });
    return () => sub.unsubscribe();
  }, [form]);

  async function next() {
    if (submitting) return;
    const isFinal = step === TOTAL_STEPS;
    if (isFinal) setSubmitting(true);
    const fields = STEP_FIELDS[step] ?? [];
    const ok = await form.trigger(fields);
    if (!ok) {
      if (isFinal) setSubmitting(false);
      return;
    }
    if (isFinal) {
      const values = form.getValues();
      const parsed = ContentProfileSchema.safeParse(values);
      if (!parsed.success || !values.month) {
        form.setError('root', { message: 'Something is missing. Check the previous rounds.' });
        setSubmitting(false);
        return;
      }
      draftStorage.save({ profile: parsed.data, month: values.month });
      navigate('/plan/results');
      return;
    }
    setDirection(1);
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function back() {
    if (step === 1) {
      navigate('/');
      return;
    }
    setDirection(-1);
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="pt-2">
      <ProgressBar step={step} total={TOTAL_STEPS} />

      <form onSubmit={(e) => e.preventDefault()}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            initial={{ opacity: 0, x: direction * 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -16 }}
            transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
          >
            {step === 1 && <StepRound1 form={form} />}
            {step === 2 && <StepRound2 form={form} />}
            {step === 3 && <StepGreat8 form={form} />}
            {step === 4 && <StepRound3 form={form} />}
          </motion.div>
        </AnimatePresence>

        {form.formState.errors.root && (
          <p className="text-sm text-danger mb-3">{form.formState.errors.root.message}</p>
        )}

        <div className="flex items-center justify-between mt-8 gap-3">
          <button
            type="button"
            className="gcm-btn gcm-btn-ghost"
            onClick={back}
            disabled={submitting}
          >
            Back
          </button>
          <button
            type="button"
            className="gcm-btn gcm-btn-primary"
            onClick={next}
            disabled={submitting}
            aria-busy={submitting}
          >
            {step === TOTAL_STEPS
              ? submitting
                ? 'Generating your plan...'
                : 'Generate my plan'
              : step === GREAT8_STEP && great8Empty
                ? 'Skip'
                : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
